import React, { useState, useEffect } from "react";
import { Phone, Mail, Globe, MapPin, Check } from "lucide-react";
import { contactInfo, services } from "../mock";
import { Reveal } from "../hooks/useReveal";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [sent, setSent] = useState(false);

  useEffect(() => { window.scrollTo({ top: 0 }); }, []);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    // Mocked submission — stored locally for the frontend preview
    const saved = JSON.parse(localStorage.getItem("ssds_enquiries") || "[]");
    saved.push({ ...form, at: new Date().toISOString() });
    localStorage.setItem("ssds_enquiries", JSON.stringify(saved));
    setSent(true);
    setForm({ name: "", email: "", phone: "", service: "", message: "" });
    setTimeout(() => setSent(false), 5000);
  };

  const field = "w-full bg-transparent border-b py-3 font-sans-ui text-[var(--navy)] placeholder:text-[var(--muted)]/70 focus:outline-none transition-colors";

  return (
    <main className="bg-[var(--cream)]" style={{ backgroundColor: "var(--cream)" }}>
      <section className="pt-40 md:pt-48 pb-24 md:pb-32">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 grid md:grid-cols-[1fr_1.1fr] gap-16 md:gap-24">
          {/* Left — info */}
          <Reveal>
            <p className="font-sans-ui tracking-wide-2 text-[0.66rem] uppercase mb-6" style={{ color: "var(--teal)" }}>
              Get in touch
            </p>
            <h1 className="font-serif-display text-[3rem] md:text-[4.6rem] leading-[1.02] font-300 text-[var(--navy)]" style={{ color: "var(--navy)" }}>
              Let&rsquo;s design your<br />next space
            </h1>
            <p className="mt-8 text-[var(--muted)] text-base md:text-lg max-w-md" style={{ color: "var(--muted)" }}>
              Tell us about your project. Our team will get back to you to discuss ideas, scope and timelines.
            </p>

            <div className="mt-12 space-y-5">
              <a href={`tel:${contactInfo.phone}`} className="flex items-center gap-4 group">
                <span className="h-11 w-11 rounded-full border flex items-center justify-center" style={{ borderColor: "var(--teal)" }}>
                  <Phone size={16} strokeWidth={1.5} style={{ color: "var(--navy)" }} />
                </span>
                <span className="link-underline text-[var(--navy)]" style={{ color: "var(--navy)" }}>{contactInfo.phone}</span>
              </a>
              <a href={`mailto:${contactInfo.email}`} className="flex items-center gap-4">
                <span className="h-11 w-11 rounded-full border flex items-center justify-center" style={{ borderColor: "var(--teal)" }}>
                  <Mail size={16} strokeWidth={1.5} style={{ color: "var(--navy)" }} />
                </span>
                <span className="link-underline text-[var(--navy)]" style={{ color: "var(--navy)" }}>{contactInfo.email}</span>
              </a>
              <div className="flex items-center gap-4">
                <span className="h-11 w-11 rounded-full border flex items-center justify-center" style={{ borderColor: "var(--teal)" }}>
                  <Globe size={16} strokeWidth={1.5} style={{ color: "var(--navy)" }} />
                </span>
                <span className="text-[var(--navy)]" style={{ color: "var(--navy)" }}>{contactInfo.website}</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="h-11 w-11 rounded-full border flex items-center justify-center" style={{ borderColor: "var(--teal)" }}>
                  <MapPin size={16} strokeWidth={1.5} style={{ color: "var(--navy)" }} />
                </span>
                <span className="text-[var(--navy)]" style={{ color: "var(--navy)" }}>{contactInfo.location}</span>
              </div>
            </div>
            <p className="mt-10 font-sans-ui tracking-wide-3 text-[0.6rem] uppercase text-[var(--muted)]" style={{ color: "var(--muted)" }}>
              GSTIN {contactInfo.gstin} · A registered company in India
            </p>
          </Reveal>

          {/* Right — form */}
          <Reveal delay={120}>
            <div className="bg-white p-8 md:p-12 shadow-[0_20px_60px_rgba(22,50,79,0.08)]">
              {sent ? (
                <div className="h-full min-h-[420px] flex flex-col items-center justify-center text-center">
                  <span className="h-16 w-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: "var(--teal)" }}>
                    <Check size={28} strokeWidth={1.5} style={{ color: "var(--cream)" }} />
                  </span>
                  <h3 className="font-serif-display text-3xl text-[var(--navy)]" style={{ color: "var(--navy)" }}>Thank you</h3>
                  <p className="mt-3 text-[var(--muted)] max-w-xs" style={{ color: "var(--muted)" }}>
                    Your enquiry has been received. We&rsquo;ll be in touch shortly.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-7">
                  <div className="grid sm:grid-cols-2 gap-7">
                    <input required placeholder="Full name" value={form.name} onChange={update("name")} className={field} style={{ borderColor: "rgba(22,50,79,0.2)" }} />
                    <input required type="email" placeholder="Email" value={form.email} onChange={update("email")} className={field} style={{ borderColor: "rgba(22,50,79,0.2)" }} />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-7">
                    <input placeholder="Phone" value={form.phone} onChange={update("phone")} className={field} style={{ borderColor: "rgba(22,50,79,0.2)" }} />
                    <select value={form.service} onChange={update("service")} className={field} style={{ borderColor: "rgba(22,50,79,0.2)" }}>
                      <option value="">Service of interest</option>
                      {services.map((s) => (<option key={s.id} value={s.title}>{s.title}</option>))}
                    </select>
                  </div>
                  <textarea required rows={4} placeholder="Tell us about your project" value={form.message} onChange={update("message")} className={field + " resize-none"} style={{ borderColor: "rgba(22,50,79,0.2)" }} />
                  <button type="submit" className="btn-line btn-navy w-full justify-center" style={{ color: "var(--navy)" }}>
                    Send enquiry
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
